import { Checkbox, Input, message, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { createCycle } from 'services/api/cycles';

const DEFAULT_OPTIONS = {
  name: '',
  keepModules: true,
  keepParticipants: true,
  keepSettings: true,
};

const DuplicateCycleModal = ({
  open,
  cycle,
  onClose,
  onDuplicated,
  onLoadingChange,
}) => {
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && cycle) {
      setOptions({
        ...DEFAULT_OPTIONS,
        name: `${cycle.name} (Copy)`,
      });
    } else if (!open) {
      setOptions(DEFAULT_OPTIONS);
      setSubmitting(false);
    }
  }, [open, cycle]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(submitting);
    }
  }, [submitting, onLoadingChange]);

  const handleSubmit = () => {
    if (!cycle) return;
    const trimmedName = options.name?.trim();
    if (!trimmedName) return;

    const newCycle = {
      name: trimmedName,
      modules:
        options.keepModules && cycle.modules ? cycle.modules : [],
      participants:
        options.keepParticipants && cycle.participants
          ? cycle.participants.map((p) => ({
              ...p,
              userId: p._id,
              ...(p.manager ? { managerId: p.manager._id } : {}),
              ...(p.peers
                ? {
                    peers: p.peers.map((peer) => ({
                      ...peer,
                      userId: peer._id,
                    })),
                  }
                : {}),
              ...(p.reports
                ? {
                    reports: p.reports.map((r) => ({
                      ...r,
                      userId: r._id,
                    })),
                  }
                : {}),
            }))
          : [],
    };

    if (options.keepSettings) {
      newCycle.isManagerReviewAfterOthers =
        cycle.isManagerReviewAfterOthers;
      newCycle.reviewVisibility = cycle.reviewVisibility;
      newCycle.reminders = cycle.reminders;
    }

    setSubmitting(true);
    createCycle(newCycle)
      .then((res) => {
        message.success(<div>Cycle duplicated successfully</div>);
        onClose?.();
        onDuplicated?.(res.data);
      })
      .catch((err) => {
        console.error('error duplicating cycle', err);
        message.error('Error duplicating cycle');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      title="Duplicate cycle"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Duplicate cycle"
      okButtonProps={{
        disabled: !options.name?.trim(),
      }}
      confirmLoading={submitting}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={{ marginBottom: 8 }}>Cycle name</div>
          <Input
            value={options.name}
            onChange={(e) =>
              setOptions((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            placeholder="Cycle name"
          />
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>
            What would you like to keep in the duplicate?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Checkbox
              checked={options.keepModules}
              onChange={(e) =>
                setOptions((prev) => ({
                  ...prev,
                  keepModules: e.target.checked,
                }))
              }
            >
              Modules
            </Checkbox>
            <Checkbox
              checked={options.keepParticipants}
              onChange={(e) =>
                setOptions((prev) => ({
                  ...prev,
                  keepParticipants: e.target.checked,
                }))
              }
            >
              Participants
            </Checkbox>
            <Checkbox
              checked={options.keepSettings}
              onChange={(e) =>
                setOptions((prev) => ({
                  ...prev,
                  keepSettings: e.target.checked,
                }))
              }
            >
              Settings
            </Checkbox>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DuplicateCycleModal;
